import { Button, Input, Space } from "antd";
import type { TableProps } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import {
  useMemo,
  useState,
  type Dispatch,
  type Key,
  type SetStateAction,
} from "react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { DELETE_ICON, EDIT_ICON, FILTER_ICON } from "src/assets/svgs";
import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import type { CustomerDevice } from "./DevicesTab";
import EpochDate from "../../../../../sipli-fleet-core/components/EpochDate/EpochDate";
import { cn } from "../../../../util/cn";

const EditIcon = EDIT_ICON as unknown as ComponentType<{
  className?: string;
}>;

const DeleteIcon = DELETE_ICON as unknown as ComponentType<{
  className?: string;
}>;

const FilterIcon = FILTER_ICON as unknown as ComponentType<{
  className?: string;
}>;

interface DevicesSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

interface DevicesTableProps {
  data: CustomerDevice[];
  isLoading: boolean;
  pageSize: number;
  currentPage: number;
  totalCount: number;
  handlePagination: (newPage: number) => void;
  handlePageSizeChange: (newSize: string | number) => void;
  sorting: DevicesSorting;
  setSorting: Dispatch<SetStateAction<DevicesSorting>>;
  columnFilters: Record<string, string>;
  onColumnSearchApply: (column: string, value: unknown) => void;
  onColumnSearchReset: () => void;
  onDeviceClick: (device: CustomerDevice) => void;
  selectionEnabled: boolean;
  selectedRowKeys: Key[];
  onSelectedRowKeysChange: (keys: Key[]) => void;
}

const deviceDataIndexToColumnId: Record<string, string> = {
  id: "imei",
  vehicle: "vehicle",
  vin: "vin",
  licensePlate: "licence_plate",
  vehicleType: "vehicle_type",
  lastDataUpdate: "lastDataUpdate", 
  fuelSensors: "fuelSensor",
  ignition: "ignition", 
  installedOn: "installed_at",
  firmware: "firmware",
};

interface DeviceIdFilterDropdownProps extends FilterDropdownProps {
  placeholder: string;
  searchLabel: string;
  resetLabel: string;
  onApply: (value: unknown) => void;
  onResetSearch: () => void;
}

const DeviceIdFilterDropdown = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  placeholder,
  searchLabel,
  resetLabel,
  onApply,
  onResetSearch,
}: DeviceIdFilterDropdownProps) => {
  const applyFilter = () => {
    onApply(selectedKeys[0]);
    confirm();
  };

  const resetFilter = () => {
    clearFilters?.();
    setSelectedKeys([]);
    onResetSearch();
    confirm();
  };

  return (
    <div className="p-2">
      <Input
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={(event) =>
          setSelectedKeys(event.target.value ? [event.target.value] : [])
        }
        onPressEnter={applyFilter}
        className="!mb-2 !block !rounded-sm"
      />

      <Space className="flex-shrink-0 h-8">
        <Button type="primary" onClick={applyFilter} className="w-28 !rounded-sm">
          {searchLabel}
        </Button>

        <Button onClick={resetFilter} className="w-28 !rounded-sm">
          {resetLabel}
        </Button>
      </Space>
    </div>
  );
};

const DevicesTable = ({
  data,
  isLoading,
  pageSize,
  currentPage,
  totalCount,
  handlePagination,
  handlePageSizeChange,
  sorting,
  setSorting,
  columnFilters,
  onColumnSearchApply,
  onColumnSearchReset,
  onDeviceClick,
  selectionEnabled,
  selectedRowKeys,
  onSelectedRowKeysChange,
}: DevicesTableProps) => {
  const { t } = useTranslation();
  const [deleteRecord, setDeleteRecord] = useState<CustomerDevice | null>(null);

  const handleTableChange: TableProps<CustomerDevice>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action === "filter") return;

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order = normalizedSorter?.order;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;

    if (!order || !dataIndex) {
      setSorting({ sortBy: "ID", sortDir: "DESC" });
      return;
    }

    const sortBy = deviceDataIndexToColumnId[String(dataIndex)];

    setSorting({
      sortBy: sortBy ?? "ID",
      sortDir: order === "ascend" ? "ASC" : "DESC",
    });
  };

  const deviceColumns: TableProps<CustomerDevice>["columns"] = useMemo(
    () => [
      {
        title: t("customer_device_id"),
        dataIndex: "id",
        key: "id",
        width: 280,
        sorter: true,
        filteredValue: columnFilters.imei ? [columnFilters.imei] : null,
        filterDropdown: (filterProps) => (
          <DeviceIdFilterDropdown
            {...filterProps}
            placeholder={`${t("search")} ${t("customer_device_id")}`}
            searchLabel={t("search")}
            resetLabel={t("reset")}
            onApply={(value) => onColumnSearchApply("imei", value)}
            onResetSearch={onColumnSearchReset}
          />
        ),
        filterIcon: () => (
          <FilterIcon className="customers-column-search-icon w-[11px] h-[11px]" />
        ),
        render: (value) => (
          <CustomTypography.CustomText className="!font-semibold">
            {value}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("customer_device_vehicle"),
        dataIndex: "vehicle",
        key: "vehicle",
        width: 180,
        sorter: true,
        render: (value) => (
          <CustomTypography.CustomText className="!font-semibold">
            {value}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("customer_device_vin"),
        dataIndex: "vin",
        key: "vin",
        width: 220,
        sorter: true,
        render: (value) => (
          <CustomTypography.CustomText className="!font-semibold">
            {value}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("customer_device_licence_plate"),
        dataIndex: "licensePlate",
        key: "licensePlate",
        width: 170,
        sorter: true,
      },
      {
        title: t("customer_device_vehicle_type"),
        dataIndex: "vehicleType",
        key: "vehicleType",
        width: 160,
        sorter: true,
      },
      {
        title: t("customer_device_last_data"),
        dataIndex: "lastDataUpdate",
        key: "lastDataUpdate",
        width: 140,
        sorter: true,
        // filteredValue: columnFilters.lastDataUpdate ? [columnFilters.lastDataUpdate] : null,
        // filterDropdown: (filterProps) => (
        //   <DeviceIdFilterDropdown
        //     {...filterProps}
        //     placeholder={`${t("search")} ${t("customer_device_last_data")}`}
        //     searchLabel={t("search")}
        //     resetLabel={t("reset")}
        //     onApply={(value) => onColumnSearchApply("lastDataUpdate", value)}
        //     onResetSearch={onColumnSearchReset}
        //   />
        // ),
        // filterIcon: () => (
        //   <FilterIcon className="customers-column-search-icon w-[11px] h-[11px]" />
        // ),
        render: (value) => <EpochDate relative date={value} appendFormat="HH:mm" />,
      },
      {
        title: t("customer_device_fuel_sensors"),
        dataIndex: "fuelSensors",
        key: "fuelSensors",
        width: 160,
        sorter: true,
        render: (value) => {
          return (
            <CustomTypography.CustomText
              className={cn("", {
                "!font-bold !text-green2": value == 1,
                "!font-bold !text-red1": value == 0,
              })}
            >
              {value == 1 ? t("on") : value == 0 ? t("off") : value}
            </CustomTypography.CustomText>
          )
        },
      },
      {
        title: t("customer_device_ignition"),
        dataIndex: "ignition",
        key: "ignition",
        width: 130,
        sorter: true,

        render: (value) => (
          <CustomTypography.CustomText
            className={cn("", {
              "!font-bold !text-green2": value == 1,
              "!font-bold !text-red1": value == 0,
            })}
          >
            {value == 1 ? t("on") : value == 0 ? t("off") : value}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("customer_device_installed_on"),
        dataIndex: "installedOn",
        key: "installedOn",
        width: 160,
        sorter: true,
        render: (value) => <EpochDate date={value} appendFormat="HH:mm" />
      },
      {
        title: t("customer_device_firmware"),
        dataIndex: "firmware",
        key: "firmware",
        width: 150,
        sorter: true,
      },
      {
        title: t("customer_device_action"),
        key: "action",
        align: "center",
        width: 120,
        render: () => (
          <div className="flex items-center justify-center gap-2 customer-actions">
            <CustomTooltip title={t("edit")}>
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("edit")}
                icon={<EditIcon className="w-[16px] h-[16px] opacity-50 disabled:pointer-events-none" />}
                onClick={(event) => event?.stopPropagation?.()}
              />
            </CustomTooltip>

            <CustomTooltip title={t("delete")}>
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("delete")}
                icon={<DeleteIcon className="w-[15px] h-[16px] opacity-50 disabled:pointer-events-none" />}
                onClick={(event) => {
                  event?.stopPropagation?.();
                  // setDeleteRecord(record);
                }}
              />
            </CustomTooltip>
          </div>
        ),
      },
    ],
    [columnFilters, onColumnSearchApply, onColumnSearchReset, t],
  );

  

  return (
    <>
      <CustomTable<CustomerDevice>
        customClass="customers-table customer-detail-devices-table"
        columns={deviceColumns}
        data={data}
        isLoading={isLoading}
        rowKey={(record) => record.vehicleId || record.id}
        rowSelection={
          selectionEnabled
            ? {
                selectedRowKeys,
                preserveSelectedRowKeys: true,
                onChange: onSelectedRowKeysChange,
              }
            : undefined
        }
        pageSize={pageSize}
        currentPage={currentPage}
        totalCount={totalCount}
        handlePagination={handlePagination}
        handlePageSizeChange={handlePageSizeChange}
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        sorting={sorting}
        onChange={handleTableChange}
        onRow={(record) => ({
          "aria-label": `${t("device")} ${record.id}`,
          tabIndex: 0,
          onClick: () => onDeviceClick(record),
          onKeyDown: (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onDeviceClick(record);
            }
          },
        })}
        scroll={{ x: selectionEnabled ? 1820 : 1770 }}
        locale={{ emptyText: t("no_data") }}
      />

      <CustomSharedConfirmationModal
        open={!!deleteRecord}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={() => setDeleteRecord(null)}
        title={t("confirm_delete_device")}
        description={t("the_deletion_operation_is_irreversible")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        centered
      />
    </>
  );
};

export default DevicesTable;
